import { findKey } from 'lodash'
import { useMemo } from 'react'
import {
    ALLOWED_ATTACHMENT_SOURCE_TYPE,
    UPLOADED_ATTACHMENT_FILE_TYPE,
} from '@OpsQuality/constants'
import {
    JobRequestAttachmentsWithCategoryType,
    FileAttachmentType,
} from '@OpsQuality/types'
import { useMutation } from '@apollo/client'
import {
    adminJobRequestQuery_jobRequest_parts_linesWithWorkOrderMapping_jobRequestLineAttachments as JobRequestAttachmentType,
    adminJobRequestQuery_jobRequest_parts_linesWithWorkOrderMapping as JobRequestLineType,
    adminJobRequestQuery_jobRequest_parts as JobRequestPartsType,
} from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'
import { updateVisibleToCustomerQuality as UpdateVisibleToCustomerType } from '@OpsQuality/JobRequestDetails/__generated__/updateVisibleToCustomerQuality'
import {
    createJobRequestAttachmentsQuality as CreateJobRequestAttachmentResultType,
    createJobRequestAttachmentsQuality_createJobRequestAttachments as CreateJobRequestAttachmentType,
} from '@OpsQuality/JobRequestDetails/__generated__/createJobRequestAttachmentsQuality'
import { removeJobRequestAttachmentJobRequestDetail as RemoveJobRequestAttachmentResultType } from '@OpsQuality/JobRequestDetails/__generated__/removeJobRequestAttachmentJobRequestDetail'

import { AllowedAttachmentSources } from '~/__generated__/globalTypes'
import logger from '~/lib/logger'
import {
    getJobRequestDetails,
    removeJobRequestAttachmentsMutation,
    updateVisibleToCustomerMutation,
    createJobRequestAttachmentsMutation,
} from '../query'
import { getFileExtension } from '~/lib/getFileExtension'
import {
    TABS,
    PARTS,
    CERTIFICATION_INSPECTION_POST_PROCESSES,
    ATTACHMENT_TYPE,
} from '../constants'
import { useMarkUploadedAttachmentMutation } from '~/gql/graphql'

export function MutationOperations() {
    const [createJobRequestAttachments] =
        useMutation<CreateJobRequestAttachmentResultType>(
            createJobRequestAttachmentsMutation,
        )

    const [markUploadedAttachment] = useMarkUploadedAttachmentMutation()

    const [removeJobRequestAttachment] =
        useMutation<RemoveJobRequestAttachmentResultType>(
            removeJobRequestAttachmentsMutation,
        )

    const [updateVisibleToCustomer] = useMutation<UpdateVisibleToCustomerType>(
        updateVisibleToCustomerMutation,
    )

    return {
        useCreateAttachment: function (
            file: FileAttachmentType,
            {
                jobRequestId,
                selectedJobRequestLineIds,
                attachmentType,
                attachmentSource,
            }: {
                jobRequestId: number
                selectedJobRequestLineIds: number[]
                attachmentType: string
                attachmentSource: AllowedAttachmentSources
            },
        ) {
            const attachmentInput = {
                jobRequestId: String(jobRequestId),
                attachment: {
                    fileName: file.name,
                    extension: getFileExtension(file.name),
                    mimeType: file.type,
                },
                type: findKey(ATTACHMENT_TYPE, t => t === attachmentType),
                jobRequestLineIds: selectedJobRequestLineIds,
                source: attachmentSource,
            }

            return createJobRequestAttachments({
                variables: {
                    attachmentInput,
                },
            })
                .then(res => res?.data?.createJobRequestAttachments)
                .then(uploadData => {
                    const upload = uploadData && uploadData[0]

                    if (!uploadData) {
                        return null
                    }

                    if (uploadData && uploadData?.length > 0) {
                        file.uploadUrl = upload?.attachment?.uploadUrl || ''
                        file.id = upload?.id
                        file.attachmentId = upload?.attachment?.id
                        file.uploadData = uploadData.filter(
                            (
                                jobRequestAttachment,
                            ): jobRequestAttachment is CreateJobRequestAttachmentType =>
                                jobRequestAttachment !== null,
                        )
                    }
                    return file
                })
                .catch((error: string) => {
                    logger.exception(error, {
                        extra: {
                            info: 'Failed to create job request attachment',
                        },
                    })
                })
        },
        useMarkUploadAttachment: function (
            file: FileAttachmentType,
            jobRequestId: number,
        ) {
            const refetchQueries = [
                {
                    query: getJobRequestDetails,
                    variables: { id: jobRequestId },
                },
            ]

            return markUploadedAttachment({
                variables: {
                    id: file?.attachmentId,
                },
                refetchQueries,
            })
                .then(res => res?.data?.markUploadedAsset)
                .then(markedData => {
                    file.attachmentId = markedData?.id
                    file.url = markedData?.downloadUrl || ''

                    return file
                })
                .catch((error: string) => {
                    logger.exception(error, {
                        extra: { info: 'Failed to markUpload' },
                    })
                })
        },
        useRemoveJobRequestAttachment: function (
            attachmentIds: string[],
            jobRequestId: number,
        ) {
            return removeJobRequestAttachment({
                variables: {
                    ids: attachmentIds,
                },
                refetchQueries: [
                    {
                        query: getJobRequestDetails,
                        variables: { id: jobRequestId },
                    },
                ],
            })
                .then(res => res.data)
                .catch((e: string) =>
                    logger.exception('Failed to delete the attachment', e),
                )
        },
        useUpdateVisibleToCustomer: async (
            id: string,
            visibleToCustomer: boolean,
            jobRequestId: number,
        ) => {
            try {
                const res = await updateVisibleToCustomer({
                    variables: {
                        id,
                        visibleToCustomer,
                    },
                    refetchQueries: [
                        {
                            query: getJobRequestDetails,
                            variables: { id: jobRequestId },
                        },
                    ],
                })

                return res.data
            } catch (err) {
                logger.exception(
                    'Failed to update visible to customer flag',
                    err,
                )
            }
        },
    }
}

export const getAllProcessTypes = (items: JobRequestLineType[]) => [
    ...new Set(items.map(item => item.processType)),
]

export const determineTab = (tab?: string) => {
    if (!tab) {
        return
    }
    let activeTab = tab.toUpperCase()

    if (TABS.indexOf(activeTab) === -1) {
        activeTab = PARTS
    }

    return activeTab
}

export const getUniqueJobRequestAttachments = (
    jobRequestAttachments: JobRequestAttachmentType[],
) => {
    const uniqJobRequestAttachments: JobRequestAttachmentType[] = []

    const attachmentDataMap = new Map()

    jobRequestAttachments.forEach(item => {
        if (!attachmentDataMap.has(item?.attachment?.id)) {
            attachmentDataMap.set(item?.attachment?.id, true)
            uniqJobRequestAttachments.push(item)
        }
    })

    return uniqJobRequestAttachments
}

export const useProvisionedAttachmentDataForTabs = (
    jobRequestLines: JobRequestLineType[],
) =>
    useMemo(() => {
        const images: JobRequestAttachmentsWithCategoryType[] = []
        const files: JobRequestAttachmentsWithCategoryType[] = []
        const allJobRequestAttachments = jobRequestLines
            .map(line => line.jobRequestLineAttachments)
            .flat()

        if (allJobRequestAttachments) {
            allJobRequestAttachments.forEach(jobRequestAttachment => {
                const { attachment } = jobRequestAttachment

                if (attachment) {
                    if (
                        attachment.mimeType?.startsWith(
                            UPLOADED_ATTACHMENT_FILE_TYPE.IMAGE_TYPE,
                        )
                    ) {
                        images.push({
                            ...jobRequestAttachment,
                        })
                    } else {
                        files.push({
                            ...jobRequestAttachment,
                        })
                    }
                }
            })
        }
        return {
            files,
            images: getUniqueJobRequestAttachments(images),
        }
    }, [jobRequestLines])

export const useJobRequestLinesByPart = (parts?: JobRequestPartsType[]) =>
    useMemo(
        () =>
            parts
                ? parts.map(part => part.linesWithWorkOrderMapping).flat()
                : [],
        [parts],
    )

export const useDigitalFictiv = (
    jobRequestLines: JobRequestLineType[],
    files: JobRequestAttachmentsWithCategoryType[],
    jobRequestId?: number,
) => {
    const digitalF5: JobRequestLineType[] = []

    jobRequestLines.forEach(jobRequestLine => {
        if (jobRequestLine.inspectionType === 'visual') {
            digitalF5.push(jobRequestLine)
        }
    })

    if (digitalF5.length > 0 && !files.find(f => f.category === 'digital-f5')) {
        files.push({
            __typename: 'WorkOrderAttachment',
            type: '',
            createdAt: '',
            workOrderItemName: '',
            createdBy: null,
            name: null,
            id: 'digital-report',
            category: 'digital-f5',
            partNames: digitalF5.map(i => i.name),
            jobRequestId: jobRequestId,
            attachment: {
                __typename: 'Asset',
                id: 'generatedRandomId',
                fileName: 'Fictiv 5 - Visual Inspection.pdf',
                downloadUrl: '',
                extension: '',
                mimeType: '',
                uploadUrl: '',
            },
            source: ALLOWED_ATTACHMENT_SOURCE_TYPE.MP,
            visibleToCustomer: null,
        })
    }

    return files
}

export const isProcessTypeCertificationOrInspection = (
    item: JobRequestLineType,
) =>
    item.platformProcessId &&
    CERTIFICATION_INSPECTION_POST_PROCESSES.includes(item.platformProcessId)

export const isProcessNeitherCertificationNorInspection = (
    item: JobRequestLineType,
) =>
    item.platformProcessId
        ? !CERTIFICATION_INSPECTION_POST_PROCESSES.includes(
              item.platformProcessId,
          )
        : true

export const usefilteredJobRequestLinesByInspectionType = (
    items: JobRequestLineType[],
) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMemo(() => {
        const attachmentTypes: {
            [
                processOrAttachmentName: string
            ]: JobRequestAttachmentsWithCategoryType[]
        } = {}
        const certificationAndInspectionTypeProcessJobRequestLines: JobRequestLineType[] =
            []

        const remainingJobRequestLines: JobRequestLineType[] = []

        items.forEach(item => {
            if (isProcessTypeCertificationOrInspection(item)) {
                certificationAndInspectionTypeProcessJobRequestLines.push(item)
            } else {
                remainingJobRequestLines.push(item)
            }
        })

        if (certificationAndInspectionTypeProcessJobRequestLines.length > 0) {
            certificationAndInspectionTypeProcessJobRequestLines.forEach(
                line => {
                    if (line?.processName) {
                        attachmentTypes[line.processName] =
                            line.jobRequestLineAttachments
                    }
                },
            )
        }

        if (remainingJobRequestLines.length > 0) {
            attachmentTypes.inspection_Photos = []

            remainingJobRequestLines.forEach(jobRequestLine => {
                if (!attachmentTypes?.fictiv5?.length) {
                    attachmentTypes.fictiv5 = []
                }
                if (!attachmentTypes?.additional_Docs?.length) {
                    attachmentTypes.additional_Docs = []
                }

                jobRequestLine.jobRequestLineAttachments.forEach(
                    (attachment: JobRequestAttachmentType) => {
                        switch (attachment?.type) {
                            case 'photos':
                                attachmentTypes.inspection_Photos.push(
                                    attachment,
                                )
                                break
                            case 'additional-docs':
                                attachmentTypes.additional_Docs.push(attachment)
                                break
                            case 'fictiv5':
                                attachmentTypes.fictiv5.push(attachment)
                                break

                            default:
                                break
                        }
                    },
                )
            })
        }
        return attachmentTypes
    }, [items])

export const getDetailString = (jobRequestLine: JobRequestLineType) => {
    const detailString = []

    if (jobRequestLine.processType === 'finishing') {
        if (jobRequestLine?.processColor) {
            detailString.push(`(${jobRequestLine?.processColor})`)
        } else if (jobRequestLine?.processNotes) {
            detailString.push(`(${jobRequestLine?.processNotes})`)
        }
    } else {
        if (jobRequestLine?.partProduct?.colorName) {
            detailString.push(`(${jobRequestLine?.partProduct?.colorName})`)
        } else if (jobRequestLine?.color) {
            detailString.push(`(${jobRequestLine?.color?.name})`)
        }
    }
    if (jobRequestLine?.processTexture) {
        detailString.push(`(${jobRequestLine.processTexture})`)
    }
    if (jobRequestLine?.processThickness) {
        detailString.push(`(${jobRequestLine.processThickness})`)
    }

    return detailString.join(' ')
}

export const isAttachmentsAvailableForCustomerView = (
    parts?: JobRequestPartsType[] | null,
) =>
    parts?.some(part =>
        part.linesWithWorkOrderMapping?.some(workOrder =>
            workOrder.jobRequestLineAttachments.some(
                attachments => attachments.visibleToCustomer,
            ),
        ),
    )
